import { useEffect, useState, type ReactNode, type FormEvent } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface WebhookRow {
  readonly webhookId: string;
  readonly webhookName: string;
  readonly eventType: string;
  readonly direction: string;
  readonly status: string;
}

export function M20WebhookManagementPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<WebhookRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [webhookName, setWebhookName] = useState('');
  const [eventType, setEventType] = useState('');
  const [targetUrl, setTargetUrl] = useState('');
  const [direction, setDirection] = useState('OUTBOUND');
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(false);

  const load = (): void => {
    apiRequest<{ data: WebhookRow[] }>('/v1/integration/webhooks', { token })
      .then((r) => setRows(r.data))
      .catch((e) => setError((e as Error).message));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  const onCreate = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setCreating(true);
    setCreated(false);
    try {
      await apiRequest('/v1/integration/webhooks', {
        method: 'POST', token,
        body: { webhookName, eventType, targetUrl, direction },
      });
      setCreated(true);
      setWebhookName('');
      setEventType('');
      setTargetUrl('');
      load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setCreating(false);
    }
  };

  const trigger = async (id: string): Promise<void> => {
    setBusyId(id + 'trigger');
    try {
      await apiRequest(`/v1/integration/webhooks/${id}/trigger`, {
        method: 'POST', token, body: { payload: {} },
      });
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  const setStatus = async (id: string, status: string): Promise<void> => {
    setBusyId(id + status);
    try {
      await apiRequest(`/v1/integration/webhooks/${id}`, {
        method: 'PATCH', token, body: { status },
      });
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  if (error && !rows) return <p role="alert">{error}</p>;
  if (!rows) return <p>{t('m20.webhook.loading')}</p>;
  return (
    <main aria-label="webhook-management">
      <h1>{t('m20.webhook.title')}</h1>

      <form onSubmit={onCreate} aria-label="webhook-create-form">
        <h2>{t('m20.webhook.newWebhook')}</h2>
        <label htmlFor="wh-name">{t('m20.webhook.name')}</label>
        <input id="wh-name" value={webhookName} onChange={(e) => setWebhookName(e.target.value)} required />
        <label htmlFor="wh-event">{t('m20.webhook.eventType')}</label>
        <input id="wh-event" value={eventType} onChange={(e) => setEventType(e.target.value)} required />
        <label htmlFor="wh-url">{t('m20.webhook.targetUrl')}</label>
        <input id="wh-url" type="url" value={targetUrl} onChange={(e) => setTargetUrl(e.target.value)} required />
        <label htmlFor="wh-direction">{t('m20.webhook.direction')}</label>
        <select id="wh-direction" aria-label="webhook-direction" value={direction} onChange={(e) => setDirection(e.target.value)}>
          <option value="OUTBOUND">{t('m20.webhook.outbound')}</option>
          <option value="INBOUND">{t('m20.webhook.inbound')}</option>
        </select>
        {error ? <p role="alert">{error}</p> : null}
        {created ? <p role="status">{t('m20.webhook.createdMsg')}</p> : null}
        <button type="submit" disabled={creating}>{creating ? t('m20.webhook.creating') : t('m20.webhook.createBtn')}</button>
      </form>

      <table aria-label="webhooks-table">
        <thead><tr><th>{t('m20.webhook.colName')}</th><th>{t('m20.webhook.colEvent')}</th><th>{t('m20.webhook.colDirection')}</th><th>{t('m20.webhook.colStatus')}</th><th>{t('m20.webhook.colActions')}</th></tr></thead>
        <tbody>
          {rows.map((w) => (
            <tr key={w.webhookId}>
              <td>{w.webhookName}</td>
              <td>{w.eventType}</td>
              <td>{w.direction}</td>
              <td>{w.status}</td>
              <td>
                <button type="button" aria-label={`trigger-${w.webhookId}`} disabled={busyId === w.webhookId + 'trigger'} onClick={() => void trigger(w.webhookId)}>{t('m20.webhook.trigger')}</button>
                {w.status === 'ACTIVE' ? (
                  <button type="button" aria-label={`disable-${w.webhookId}`} disabled={busyId === w.webhookId + 'DISABLED'} onClick={() => void setStatus(w.webhookId, 'DISABLED')}>{t('m20.webhook.disable')}</button>
                ) : (
                  <button type="button" aria-label={`enable-${w.webhookId}`} disabled={busyId === w.webhookId + 'ACTIVE'} onClick={() => void setStatus(w.webhookId, 'ACTIVE')}>{t('m20.webhook.enable')}</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </main>
  );
}
